import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { devError } from "@/lib/logger";
import { marketService } from "@/services";
import { TrendingUp, Sparkles, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const relTime = (iso) => {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
  } catch {
    return "";
  }
};

export default function MarketFeed() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Recent-history panel: always show the last N results regardless of
      // recency, so it doesn't blank out during quiet periods. (The backend
      // returns newest-first by `limit` when no time window is passed.)
      const data = await marketService.getFeed({ limit: 8 });
      setItems(data.items || []);
    } catch (err) {
      devError("Failed to load market feed:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, []);

  return (
    <aside
      className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-5 max-h-[60vh] lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] overflow-hidden flex flex-col"
      data-testid="market-feed"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="relative">
            <TrendingUp className="w-5 h-5 text-[#38B6FF]" />
            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#FF5A5F] animate-pulse" />
          </div>
          <h3 className="text-base font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }}>
            {t("marketFeed.title")}
          </h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={load}
          data-testid="market-feed-refresh"
          disabled={loading}
          aria-label={t("marketFeed.label.refresh")}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="space-y-2 overflow-y-auto scroll-thin flex-1 pr-1">
        {items.length === 0 ? (
          <div className="text-center py-8 text-[#1A237E]/40 dark:text-[#E3F2FD]/40 text-sm">
            <Sparkles className="w-6 h-6 mx-auto mb-2 opacity-40" />
            {t("marketFeed.empty")}
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className="bg-white/60 dark:bg-slate-900/60 rounded-xl px-3 py-2 border border-[#1A237E]/5 dark:border-[#E3F2FD]/5 hover:border-[#38B6FF]/20 transition-colors"
              data-testid={`feed-item-${item.id}`}
            >
              <div className="flex items-start gap-2">
                <span className="text-base leading-none mt-0.5">{item.emoji || "•"}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#1A237E] dark:text-[#E3F2FD] leading-snug break-words">{item.message}</p>
                  <p className="text-[10px] text-[#1A237E]/40 dark:text-[#E3F2FD]/40 mt-1">{t("marketFeed.ago", { time: relTime(item.created_at) })}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </aside>
  );
}
